import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col } from "react-bootstrap";
import { useDates } from "../../../dates/DatesContext";
import useSelectedMetrics from "../../../components/custom/merchandise/hooks/useSelectedMetrics";
import MultiMetricChart from "../../../components/custom/merchandise/MultiMetricChart";
import Loading from "../../../components/core/blocks/Loading";
import getMetricSpec from "../../merchandising/utils/metrics";
import DetailedMetric from "../../../components/core/blocks/DetailedMetric";
import { useAcosOrRoas } from "../../../utils/acosOrRoas";
import moment from "moment";
import { getAdTypeName } from "../../../utils/getAdTypeName";
import { useAppSelector } from "../../../redux/hooks";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { SeriesLegendItemClickEventObject } from "highcharts/highcharts.src";
import { useAdTotals } from "../../perf_reports/api/ads/useAdTotals";
import { useGetV4AdvReportByDate } from "../../perf_reports/api/ads/v4AdvReportByDate";
import { useAdPlanDetails } from "../../perf_reports/api/ads/useAdPlanDetails";

interface Props {
  adId?: number;
  highlightDate?: string | null;
}

const AdvertisementExpander = ({ adId, highlightDate = null }: Props) => {
  const { t } = useTranslation(["advertising"]);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const acosOrRoas = useAcosOrRoas();

  const { group } = useDates();
  const hasAdId = adId != null;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion -- defensive: prop may be string at runtime
  const adIdNumber = hasAdId ? Number(adId) : 0;

  const adIdFilters = useMemo(
    () =>
      hasAdId ?
        [
          {
            id: "ad_id",
            value: { op: "=", value: adIdNumber },
          },
        ]
      : [],
    [hasAdId, adIdNumber],
  );

  const adTotalsQuery = useQuery(
    useAdTotals(
      {
        adId: adIdNumber,
      },
      { enabled: hasAdId },
    ),
  );

  const adByDateOptionsGetter = useGetV4AdvReportByDate();
  const adTimelineQuery = useQuery({
    ...adByDateOptionsGetter({
      filters: adIdFilters,
    }),
    enabled: hasAdId,
    placeholderData: keepPreviousData,
  });

  const adPlanQuery = useQuery(
    useAdPlanDetails(
      {
        adId: adIdNumber,
      },
      { enabled: hasAdId },
    ),
  );

  const adPlanData = useMemo(() => {
    const plan = adPlanQuery.data?.data[0];

    if (!plan) {
      return undefined;
    }

    return plan;
  }, [adPlanQuery.data]);

  const timeline = adTimelineQuery.data?.data ?? [];
  const totals = adTotalsQuery.data?.data;

  const isLoading = adTotalsQuery.isLoading || adTimelineQuery.isLoading || adPlanQuery.isLoading;

  const { selectedMetrics, toggleMetric } = useSelectedMetrics(
    ["ad_sales", "ad_spend", "ctr"],
    "advertisementExpander",
  );

  const metrics = useMemo(
    () => [
      getMetricSpec("ad_sales"),
      getMetricSpec("ad_spend"),
      acosOrRoas(getMetricSpec("acos"), getMetricSpec("roas")),
      getMetricSpec("clicks"),
      getMetricSpec("impressions"),
      getMetricSpec("ctr"),
    ],
    [acosOrRoas],
  );

  const handleLegendChange = (e: SeriesLegendItemClickEventObject) => {
    const id = e.target.options.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  const dateFilterWithoutToday = timeline.filter((d) => moment().format("YYYY-MM-DD") !== d.start_date);

  if (isLoading) {
    return <Loading fullPage={false} height={"300px"} />;
  }

  return (
    <div className="bg-white">
      <Row className="pt-3">
        <Col xs={12} md={6}>
          <h3
            className="pb-4 px-5"
            style={{
              fontSize: 22,
            }}
          >
            {adPlanData?.ad_name} -{" "}
            {(() => {
              const sel = getAdTypeName(adPlanData?.ad_type, channel);
              return sel ? t(sel) : "";
            })()}
          </h3>
        </Col>
        <Col xs={12}>
          <MultiMetricChart
            metrics={metrics}
            selectedMetrics={selectedMetrics}
            data={dateFilterWithoutToday}
            group={group}
            handleLegendChange={handleLegendChange}
            showAnnotations={false}
            highlightDate={highlightDate}
          />
        </Col>
        <Col xs={12}>
          <Row className="pt-4">
            {metrics.slice(0, 6).map((m, i) => {
              return (
                <Col key={m.id} className={`mb-4 ${i % 2 === 0 ? "ps-2" : "pe-2"}`} xs={12} md={6} lg={2}>
                  <div className="">
                    <div>
                      <DetailedMetric center metric={m} data={totals} showTarget={false} />
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default AdvertisementExpander;
